'use client'
import {
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@core/ui-elements'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdOutlineKeyboardArrowDown } from 'react-icons/md'

export type ISelectDropdownItem = {
  id: string | null | string[]
  alias: string
}
export type ISelectDropdown = {
  list: ISelectDropdownItem[]
  onSelect: (hub: ISelectDropdownItem | null) => void
  defaultIdValue?: ISelectDropdownItem | null
  placeholder?: string
  emptyItem?: ISelectDropdownItem
}

export const SelectDropdown: React.FC<ISelectDropdown> = ({
  list,
  onSelect,
  defaultIdValue = null,
  placeholder,
  emptyItem,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState<ISelectDropdownItem | null>(defaultIdValue)

  useEffect(
    function syncValue() {
      if (defaultIdValue !== value) {
        setValue(defaultIdValue)
      }
    },
    [defaultIdValue, value]
  )

  const handleChangeSelected = useCallback(
    (item: ISelectDropdownItem | null) => {
      onSelect(item)
      setValue(item)
    },
    [onSelect]
  )

  const buttonText = useMemo(
    () => (value ? value.alias : placeholder),
    [value, placeholder]
  )

  const listItems = useMemo(() => {
    let newList = []
    if (emptyItem) {
      newList.push(emptyItem)
    }
    newList = newList.concat(list)
    return newList
  }, [list, emptyItem])

  const colorButtonText = useMemo(() => {
    if (!value) {
      return 'gray.400'
    }

    if (isOpen) {
      return 'blue.500'
    }

    return 'gray.800'
  }, [value, isOpen])

  return (
    <Menu
      matchWidth
      isOpen={isOpen}
      variant="hubTO"
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
    >
      <MenuButton backgroundColor="white" width="100%">
        <Flex
          alignItems={'center'}
          flexDirection={'row'}
          justifyContent="space-between"
          width={'100%'}
        >
          <Text
            as={'span'}
            color={colorButtonText}
            marginLeft={'0.75rem'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            whiteSpace={'nowrap'}
          >
            {buttonText}
          </Text>

          <Icon
            as={MdOutlineKeyboardArrowDown}
            transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
            transition="transform 0.3s ease"
          />
        </Flex>
      </MenuButton>
      <MenuList maxHeight="60dvh" minW="100%">
        {listItems.map((item) => (
          <MenuItem
            key={`${item.id}`}
            aria-selected={value?.id === item.id}
            onClick={() => handleChangeSelected(item)}
          >
            <Text
              as={'span'}
              color={value?.id === item.id ? 'blue.500' : undefined}
              flex={1}
              overflow={'hidden'}
              textOverflow={'ellipsis'}
              whiteSpace={'nowrap'}
            >
              {item.alias}
            </Text>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  )
}
